// Admin: all operators' personal API tokens (MCP agents). Read-only
// overview + revoke — issuing happens per-operator from their own
// Settings page (gated by api_token.issue), not here.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { apiTokensApi, type ApiTokenAdminRead } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

type TokenStatus = "active" | "revoked" | "expired";

function tokenStatus(t: ApiTokenAdminRead): TokenStatus {
  if (t.revoked_at) return "revoked";
  if (t.expires_at && new Date(t.expires_at).getTime() < Date.now()) return "expired";
  return "active";
}

export default function ApiTokensAdmin() {
  return (
    <RequirePermission codes="user.manage">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["admin", "api-tokens"],
    queryFn: () => apiTokensApi.adminListAll(),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => apiTokensApi.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "api-tokens"] }),
  });

  return (
    <>
      <PageHeader
        eyebrow={t("eyebrows.adminPeopleAccess")}
        title={t("apiTokensAdmin.title")}
        description={t("apiTokensAdmin.description")}
      />

      {q.isLoading && <LoadingState />}
      {q.isError && <ErrorState error={q.error} />}

      {q.data && q.data.items.length === 0 && (
        <EmptyState title={t("apiTokensAdmin.empty")} />
      )}

      {q.data && q.data.items.length > 0 && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-4 py-3">{t("apiTokensAdmin.colOwner")}</th>
                <th className="px-4 py-3">{t("apiTokens.colName")}</th>
                <th className="px-4 py-3">{t("apiTokens.colCreated")}</th>
                <th className="px-4 py-3">{t("apiTokens.colLastUsed")}</th>
                <th className="px-4 py-3">{t("apiTokens.colExpires")}</th>
                <th className="px-4 py-3">{t("apiTokens.colStatus")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((tok) => (
                <TokenRow key={tok.id} tok={tok} onRevoke={() => revoke.mutate(tok.id)} pending={revoke.isPending} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function TokenRow({
  tok,
  onRevoke,
  pending,
}: {
  tok: ApiTokenAdminRead;
  onRevoke: () => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const status = tokenStatus(tok);

  return (
    <tr className="border-t border-border hover:bg-surface-hover">
      <td className="px-4 py-3">
        <div className="text-text-primary">{tok.operator_name}</div>
        <div className="text-text-muted text-xs">{tok.operator_role}</div>
      </td>
      <td className="px-4 py-3">{tok.name}</td>
      <td className="px-4 py-3 text-text-muted text-xs whitespace-nowrap">
        {formatDateTime(tok.created_at)}
      </td>
      <td className="px-4 py-3 text-text-muted text-xs whitespace-nowrap">
        {tok.last_used_at ? formatDateTime(tok.last_used_at) : "—"}
      </td>
      <td className="px-4 py-3 text-text-muted text-xs whitespace-nowrap">
        {tok.expires_at ? formatDateTime(tok.expires_at) : t("apiTokens.noExpiry")}
      </td>
      <td className="px-4 py-3">
        <span
          className={
            "pill text-xs " +
            (status === "active" ? "pill-ok" : status === "expired" ? "pill-warn" : "pill-danger")
          }
        >
          {t(`apiTokens.status.${status}`)}
        </span>
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        {status === "active" && (
          confirming ? (
            <span className="inline-flex items-center gap-1">
              <span className="text-text-muted text-xs">{t("apiTokens.confirmRevoke")}</span>
              <button
                type="button"
                className="btn btn-danger text-xs"
                onClick={onRevoke}
                disabled={pending}
              >
                {pending ? t("common.deleting") : t("common.yes")}
              </button>
              <button type="button" className="btn text-xs" onClick={() => setConfirming(false)}>
                {t("common.no")}
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="btn btn-danger text-xs"
              onClick={() => setConfirming(true)}
            >
              {t("apiTokens.revoke")}
            </button>
          )
        )}
      </td>
    </tr>
  );
}
